import * as Sentry from '@sentry/angular';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import localeCs from '@angular/common/locales/cs';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './auth/interceptors/auth.interceptor';
import { licenseInterceptor } from './core/interceptors/license.interceptor';
import { problemDetailInterceptor } from './core/interceptors/problem-detail.interceptor';
import { aiChargeInterceptor } from './core/interceptors/ai-charge.interceptor';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CzechPaginatorIntl } from './core/table/czech-paginator-intl';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { registerLocaleData } from '@angular/common';
import { environment } from '../environments/environment';
import { reloadOnChunkLoadError } from './core/chunk-reload/chunk-reload';

registerLocaleData(localeCs);

const sentryProviders = environment.production
  ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
    ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withNavigationErrorHandler(({ error, url }) => reloadOnChunkLoadError(error, url)),
    ),
    { provide: LOCALE_ID, useValue: 'cs-CZ' },
    { provide: MAT_DATE_LOCALE, useValue: 'cs-CZ' },
    { provide: MatPaginatorIntl, useClass: CzechPaginatorIntl },
    provideLuxonDateAdapter(),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        licenseInterceptor,
        aiChargeInterceptor,
        problemDetailInterceptor,
      ]),
    ),
    ...sentryProviders,
  ],
};
